import React, { useEffect, useState } from 'react';
import { useNetwork } from '@/providers/NetworkProvider';
import { formatHashPower, formatToken } from '@/utils/format';
import '@/styles/leaderboard.css';

interface LeaderboardEntry {
  rank: number;
  player: string;
  totalNodes: number;
  totalHashPower: number;
  totalRewardsClaimed: string;
  achievementPoints: number;
  lastUpdated: number;
}

interface SeasonInfo {
  seasonNumber: number;
  startTime: number;
  endTime: number;
  isActive: boolean;
}

interface LeaderboardStats {
  totalPlayersCount: number;
  totalNodes: number;
  totalHashPower: number;
  totalRewardsClaimed: string;
}

interface LeaderboardPanelProps {
  onClose: () => void;
}

export const LeaderboardPanel: React.FC<LeaderboardPanelProps> = ({ onClose }) => {
  const { contracts, signer, address } = useNetwork();
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [seasonInfo, setSeasonInfo] = useState<SeasonInfo | null>(null);
  const [leaderboardStats, setLeaderboardStats] = useState<LeaderboardStats | null>(null);
  const [userRank, setUserRank] = useState<number | null>(null);
  const [lastRefresh, setLastRefresh] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [seasonError, setSeasonError] = useState(false);

  useEffect(() => {
    if (!signer || !contracts?.leaderboard) return;
    void loadAll();
  }, [signer, contracts, address]);

  useEffect(() => {
    if (!signer || !contracts?.leaderboard) return;
    const id = setInterval(() => {
      void loadLeaderboard();
      void loadUserRank();
    }, 30000);
    return () => clearInterval(id);
  }, [signer, contracts, address]);

  const loadAll = async () => {
    await Promise.all([loadLeaderboard(), loadSeasonInfo(), loadUserRank(), loadLeaderboardStats()]);
    setLastRefresh(Date.now());
  };

  const loadLeaderboard = async () => {
    if (!contracts?.leaderboard) return;

    try {
      setLoading(true);
      const topPlayers = await contracts.leaderboard.getTopPlayers(20);
      const leaderboardData = topPlayers
        .map((player: any) => ({
          rank: Number(player.rank),
          player: player.player,
          totalNodes: Number(player.totalNodes),
          totalHashPower: Number(player.totalHashPower),
          totalRewardsClaimed: formatToken(player.totalRewardsClaimed.toString()),
          achievementPoints: Number(player.achievementPoints),
          lastUpdated: Number(player.lastUpdated),
        }))
        .sort((a: LeaderboardEntry, b: LeaderboardEntry) => a.rank - b.rank);

      setLeaderboard(leaderboardData);
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
      setLeaderboard([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSeasonInfo = async () => {
    if (!contracts?.leaderboard) return;

    try {
      setSeasonError(false);
      const seasonData = await contracts.leaderboard.getCurrentSeason();
      setSeasonInfo({
        seasonNumber: Number(seasonData.seasonNumber),
        startTime: Number(seasonData.startTime),
        endTime: Number(seasonData.endTime),
        isActive: seasonData.isActive,
      });
    } catch (error) {
      console.error('Failed to load season info:', error);
      setSeasonInfo(null);
      setSeasonError(true);
    }
  };

  const loadUserRank = async () => {
    if (!contracts?.leaderboard || !address) return;

    try {
      const rank = await contracts.leaderboard.getPlayerRank(address);
      const parsed = Number(rank);
      setUserRank(parsed > 0 ? parsed : null);
    } catch (error) {
      console.error('Failed to load user rank:', error);
      setUserRank(null);
    }
  };

  const loadLeaderboardStats = async () => {
    if (!contracts?.leaderboard) return;

    try {
      const stats = await contracts.leaderboard.getLeaderboardStats();
      setLeaderboardStats({
        totalPlayersCount: Number(stats.totalPlayersCount),
        totalNodes: Number(stats.totalNodes),
        totalHashPower: Number(stats.totalHashPower),
        totalRewardsClaimed: formatToken(stats.totalRewardsClaimed.toString()),
      });
    } catch (error) {
      console.error('Failed to load leaderboard stats:', error);
      setLeaderboardStats(null);
    }
  };

  const formatTimeRemaining = (endTime: number) => {
    const now = Math.floor(Date.now() / 1000);
    const diff = endTime - now;

    if (diff <= 0) return 'ENDED';

    const days = Math.floor(diff / 86400);
    const hours = Math.floor((diff % 86400) / 3600);
    const minutes = Math.floor((diff % 3600) / 60);

    if (days > 0) return `${days}D ${hours}H ${minutes}M`;
    if (hours > 0) return `${hours}H ${minutes}M`;
    return `${minutes}M`;
  };

  const formatAddress = (value: string) => `${value.slice(0, 6)}...${value.slice(-4)}`;

  const formatLastUpdated = (timestamp: number) => {
    if (!timestamp) return 'N/A';

    const nowSec = Math.floor(Date.now() / 1000);
    const diff = nowSec - timestamp;
    if (diff < 0) return 'just now';

    const minutes = Math.floor(diff / 60);
    const hours = Math.floor(diff / 3600);
    const days = Math.floor(diff / 86400);

    if (days > 0) return `${days}d`;
    if (hours > 0) return `${hours}h`;
    return `${minutes}m`;
  };

  const getRowClass = (entry: LeaderboardEntry) => {
    const classes = ['lb-row'];
    if (entry.rank === 1) classes.push('lb-row-gold');
    else if (entry.rank === 2) classes.push('lb-row-silver');
    else if (entry.rank === 3) classes.push('lb-row-bronze');
    if (address && entry.player.toLowerCase() === address.toLowerCase()) classes.push('lb-row-you');
    return classes.join(' ');
  };

  return (
    <div className="lb-panel">
      <div className="lb-header">
        <div className="lb-title">LEADERBOARD</div>
        <div className="lb-header-right">
          <span className="lb-chip">
            {seasonInfo
              ? `SEASON #${seasonInfo.seasonNumber}`
              : seasonError
              ? 'SEASON: SYNC ERROR'
              : 'SEASON: LOADING...'}
          </span>
          <button className="lb-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>
      </div>

      {(seasonInfo || leaderboardStats) && (
        <div className="lb-statcards">
          {seasonInfo && (
            <>
              <div className="lb-statcard">
                <span className="lb-statcard-label">SEASON</span>
                <span className="lb-statcard-value">#{seasonInfo.seasonNumber}</span>
                <span className={`lb-statcard-tag ${seasonInfo.isActive ? 'live' : 'ended'}`}>
                  {seasonInfo.isActive ? 'ACTIVE' : 'ENDED'}
                </span>
              </div>
              <div className="lb-statcard">
                <span className="lb-statcard-label">{seasonInfo.isActive ? 'ENDS IN' : 'ENDED'}</span>
                <span className="lb-statcard-value lb-statcard-amber">
                  {seasonInfo.isActive
                    ? formatTimeRemaining(seasonInfo.endTime)
                    : formatLastUpdated(seasonInfo.endTime)}
                </span>
              </div>
            </>
          )}
          {leaderboardStats && (
            <>
              <div className="lb-statcard">
                <span className="lb-statcard-label">PLAYERS</span>
                <span className="lb-statcard-value">{leaderboardStats.totalPlayersCount}</span>
              </div>
              <div className="lb-statcard">
                <span className="lb-statcard-label">NODES</span>
                <span className="lb-statcard-value">{leaderboardStats.totalNodes}</span>
              </div>
              <div className="lb-statcard">
                <span className="lb-statcard-label">HASH</span>
                <span className="lb-statcard-value">{formatHashPower(leaderboardStats.totalHashPower)}</span>
              </div>
              <div className="lb-statcard">
                <span className="lb-statcard-label">REWARDS</span>
                <span className="lb-statcard-value">{leaderboardStats.totalRewardsClaimed}</span>
              </div>
            </>
          )}
        </div>
      )}

      <div className="lb-content">
        <div className="lb-table-header">
          <div className="lb-cell lb-rank-cell">RANK</div>
          <div className="lb-cell lb-player-cell">PLAYER</div>
          <div className="lb-cell lb-stat-cell">NODES</div>
          <div className="lb-cell lb-stat-cell">HASH</div>
          <div className="lb-cell lb-stat-cell">REWARDS</div>
          <div className="lb-cell lb-stat-cell">POINTS</div>
          <div className="lb-cell lb-time-cell">LAST SEEN</div>
        </div>

        <div className="lb-table-body">
          {leaderboard.length === 0 && !loading && (
            <div className="lb-empty">
              <div className="lb-empty-title">NO PLAYERS RANKED YET</div>
              <div className="lb-empty-subtitle">
                Activate a node and claim rewards to sync onto the board.
              </div>
            </div>
          )}

          {loading && leaderboard.length === 0 && (
            <div className="lb-empty">
              <div className="lb-empty-title">SYNCING RANKS…</div>
              <div className="lb-empty-subtitle">Pulling top players from chain.</div>
            </div>
          )}

          {leaderboard.map((entry) => (
            <div key={entry.player} className={getRowClass(entry)}>
              <div className="lb-rank-cell">
                <span className="lb-rank-badge">#{entry.rank}</span>
              </div>
              <div className="lb-player-cell">
                <span className="lb-player-address">{formatAddress(entry.player)}</span>
                {address && entry.player.toLowerCase() === address.toLowerCase() && (
                  <span className="lb-you-tag">YOU</span>
                )}
              </div>
              <div className="lb-stat-cell"><span className="lb-stat-value">{entry.totalNodes}</span></div>
              <div className="lb-stat-cell"><span className="lb-stat-value">{formatHashPower(entry.totalHashPower)}</span></div>
              <div className="lb-stat-cell"><span className="lb-stat-value">{entry.totalRewardsClaimed}</span></div>
              <div className="lb-stat-cell"><span className="lb-stat-value">{entry.achievementPoints}</span></div>
              <div className="lb-time-cell"><span className="lb-time-value">{formatLastUpdated(entry.lastUpdated)}</span></div>
            </div>
          ))}
        </div>
      </div>

      <div className="lb-footer">
        {userRank !== null && (
          <div className="lb-footer-block">
            <span className="lb-footer-label">YOUR RANK</span>
            <span className="lb-your-rank-number">#{userRank}</span>
            <span className="lb-chip">SEASON {seasonInfo?.seasonNumber || 'N/A'}</span>
          </div>
        )}
        {!loading && lastRefresh > 0 && (
          <div className="lb-footer-block lb-footer-refresh">
            <span className="lb-footer-label">LAST REFRESH</span>
            <span className="lb-refresh-value">{new Date(lastRefresh).toLocaleTimeString()}</span>
          </div>
        )}
      </div>

      {loading && (
        <div className="lb-loading-overlay">
          <div className="hacker-loading"></div>
          <span>LOADING LEADERBOARD...</span>
        </div>
      )}
    </div>
  );
};
